import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Activity } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchStatus } from '../store/statusSlice'
import AppEventFeed from '../components/AppEventFeed'
import WebhookEventFeed from '../components/WebhookEventFeed'
import DefaultPromptDisplay from '../components/DefaultPromptDisplay'
import ApiTokenControls from '../components/ApiTokenControls'
import McpConnectionInfo from '../components/McpConnectionInfo'
import ContainerTable from '../components/ContainerTable'
import TaskRequestFeed from '../components/TaskRequestFeed'
import WebhookHelpButton from '../components/WebhookHelpButton'
import './StatusPage.css'

const POLL_INTERVAL = 10000

function StatusPage() {
  const dispatch = useAppDispatch()
  const { webhookEvents, containerFilter, minContainers, maxContainers, loading, error } = useAppSelector(
    (state) => state.status
  )
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    dispatch(fetchStatus(containerFilter))
    intervalRef.current = setInterval(() => {
      dispatch(fetchStatus(containerFilter))
    }, POLL_INTERVAL)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [dispatch, containerFilter])

  return (
    <main className="status-page" data-testid="status-page">
      <nav className="status-page__sticky-header" aria-label="Status page">
        <Link to="/" className="status-page__back" data-testid="status-back-link">
          <ArrowLeft size={16} aria-hidden="true" />
          Back
        </Link>
        <header className="status-page__header">
          <div className="status-page__header-left">
            <div className="status-page__icon" aria-hidden="true">
              <Activity size={20} />
            </div>
            <div>
              <h1 className="status-page__title">System Status</h1>
              <p className="status-page__subtitle">Build configuration and recent webhook activity</p>
            </div>
          </div>
          <WebhookHelpButton />
        </header>
      </nav>

      {loading && (
        <div className="status-page__loading" data-testid="status-page-loading" role="status" aria-label="Loading status">
          Loading status...
        </div>
      )}
      {error && !loading && (
        <div className="status-page__error" data-testid="status-page-error" role="alert">
          {error}
        </div>
      )}
      {!loading && (
        <div className="status-page__content">
          <details className="status-page__section" open>
            <summary className="status-page__section-toggle">Build Configuration</summary>
            <DefaultPromptDisplay minContainers={minContainers} maxContainers={maxContainers} />
          </details>
          <details className="status-page__section">
            <summary className="status-page__section-toggle">MCP Server</summary>
            <div className="status-page__mcp-tokens">
              <h3 className="status-page__mcp-tokens-title">API keys</h3>
              <p className="status-page__mcp-tokens-desc">
                Mint an <code>lbak_…</code> API key for MCP clients. Pass it as{' '}
                <code>Authorization: Bearer &lt;key&gt;</code>.
              </p>
              <ApiTokenControls />
            </div>
            <McpConnectionInfo />
          </details>
          <details className="status-page__section">
            <summary className="status-page__section-toggle">All Containers</summary>
            <ContainerTable />
          </details>
          <details className="status-page__section">
            <summary className="status-page__section-toggle">Recent Task Requests</summary>
            <TaskRequestFeed />
          </details>
          <div className="status-page__section">
            <AppEventFeed />
          </div>
          <div className="status-page__section">
            <WebhookEventFeed events={webhookEvents} />
          </div>
        </div>
      )}
    </main>
  )
}

export default StatusPage
